import { ROUTES } from "@/constants";
import { usePathWithParams } from "@/hooks";
import { useRouter } from "next/navigation";
import { useEffect, useMemo, useState, useTransition } from "react";
import type { DateSelectProps } from "./DateSelect";

export const useDateSelect = ({ dates = [], orderId, userId, slug }: DateSelectProps) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPending, startTransition] = useTransition();
  const router = useRouter();
  const pathWithParams = usePathWithParams();

  const lastIndex = useMemo(() => Math.max(0, dates.length - 1), [dates.length]);
  const path = (orderId: string) => pathWithParams(`${ROUTES.users.userOrders(userId)}/${orderId}/${slug}`);

  useEffect(() => {
    if (!dates.length) {
      setCurrentIndex(0);
      return;
    }
    const idx = dates.findIndex(d => d.orderId === orderId);
    setCurrentIndex(idx >= 0 ? idx : 0);
  }, [dates, orderId]);

  const handleSelectChange = (value: string) => {
    startTransition(() => router.push(path(value)));
  };

  const handleArrowLeft = () => {
    startTransition(() => {
      if (currentIndex > 0 && dates.length) {
        const newOrderId = dates[currentIndex - 1].orderId;
        router.push(path(newOrderId));
      }
    });
  };

  const handleArrowRight = () => {
    startTransition(() => {
      if (currentIndex < lastIndex && dates.length) {
        const newOrderId = dates[currentIndex + 1].orderId;
        router.push(path(newOrderId));
      }
    });
  };

  return { isPending, currentIndex, handleArrowLeft, handleArrowRight, handleSelectChange };
};
